/**
 * A person's own voice (ADR-117 in `platform`): the voice ElevenLabs makes from a recording the
 * person makes of themselves, which then speaks the voicemail greetings left in their name.
 *
 * This file is the part with no browser in it: what a recording has to be, how it is written as
 * the WAV core-api reads, and the sentences the page says about it. The microphone is in
 * `own-voice-recorder.ts`.
 *
 * core-api takes a WAV of plain 16-bit sound and reads its length from its header
 * (`own_voice.read_wav`). A browser records Opus or AAC, by the browser, so the console decodes
 * what it recorded and writes that WAV itself (`encodeWav`): one channel, `SAMPLE_RATE`. The
 * limits here are core-api's, which sends them with `GET /auth/me/voice`; the constants are what
 * the page shows before that answer is in.
 */

/** What the recording is written at: wideband speech, and under 8 MB for two minutes. */
export const SAMPLE_RATE = 32_000;
/** How long a recording has to be, in seconds (core-api's `MIN_SECONDS` and `MAX_SECONDS`). */
export const MIN_SECONDS = 30;
export const MAX_SECONDS = 180;
/** How long the page asks for: about a minute makes the best voice. */
export const AIM_SECONDS = 60;
/** The recorder stops by itself here, short of the limit, so a take is never refused for length. */
export const STOP_SECONDS = 150;

/** What a person reads aloud: about a minute at a calm pace, in the words of their own calls. */
export const READING: Record<"en" | "es", { label: string; text: string }> = {
  en: {
    label: "English",
    text:
      "Hi, this is me, calling from the office. I am reaching out about the form you sent us after your "
      + "accident. First, I hope you are feeling better today. I know this is a stressful time, and I want to "
      + "make it a little easier. Based on what you told us, you may be entitled to compensation, and it costs "
      + "you nothing to find out. I only need a few minutes to go over what happened, where it happened, and "
      + "how you have been since. If now is not a good time, that is no problem at all. You can call or text "
      + "us back whenever it suits you, morning or evening, any day of the week. We are here to help, and "
      + "there is no pressure. Thank you for your time, take care of yourself, and I look forward to "
      + "speaking with you soon.",
  },
  es: {
    label: "Spanish",
    text:
      "Hola, le llamo de la oficina. Me comunico con usted por el formulario que nos envió después de su "
      + "accidente. Antes que nada, espero que hoy se sienta mejor. Sé que es un momento difícil y quiero "
      + "hacérselo un poco más fácil. Por lo que nos contó, puede tener derecho a una compensación, y "
      + "averiguarlo no le cuesta nada. Solo necesito unos minutos para repasar qué pasó, dónde pasó y cómo "
      + "ha estado desde entonces. Si ahora no es un buen momento, no hay ningún problema. Puede llamarnos o "
      + "enviarnos un mensaje cuando le convenga, por la mañana o por la tarde, cualquier día de la semana. "
      + "Estamos para ayudarle, sin ninguna presión. Gracias por su tiempo, cuídese mucho, y espero hablar "
      + "con usted pronto.",
  },
};

/** Several channels as one, by their average. One channel is returned as it is. */
export function mixToMono(channels: readonly Float32Array[]): Float32Array {
  const [first, ...rest] = channels;
  if (first === undefined) return new Float32Array(0);
  if (rest.length === 0) return first;
  const length = Math.min(...channels.map((c) => c.length));
  const mono = new Float32Array(length);
  for (const channel of channels) {
    for (let i = 0; i < length; i += 1) mono[i] = (mono[i] ?? 0) + (channel[i] ?? 0) / channels.length;
  }
  return mono;
}

/**
 * Sound as the WAV core-api takes: one channel of 16-bit samples at `rate`, behind the 44-byte
 * header that says so. A sample past full scale is held at full scale, never wrapped.
 */
export function encodeWav(samples: Float32Array, rate: number = SAMPLE_RATE): ArrayBuffer {
  const bytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + bytes);
  const view = new DataView(buffer);
  const ascii = (at: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(at + i, text.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + bytes, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true); // the format block's own length
  view.setUint16(20, 1, true); // plain samples, not compressed
  view.setUint16(22, 1, true); // one channel
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true); // bytes a second
  view.setUint16(32, 2, true); // bytes a sample
  view.setUint16(34, 16, true); // bits a sample
  ascii(36, "data");
  view.setUint32(40, bytes, true);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(44 + i * 2, Math.round(s < 0 ? s * 0x8000 : s * 0x7fff), true);
  }
  return buffer;
}

/** How long a WAV written by `encodeWav` is, in seconds. */
export function wavSeconds(bytes: number, rate: number = SAMPLE_RATE): number {
  return Math.max(0, bytes - 44) / (rate * 2);
}

/** A length of time as a clock reads it: 62 seconds is "1:02". */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** Why a take cannot be used, or null. `limits` are core-api's when they are known. */
export function takeProblem(
  seconds: number,
  limits: { min_seconds?: number; max_seconds?: number } = {},
): string | null {
  const min = limits.min_seconds ?? MIN_SECONDS;
  const max = limits.max_seconds ?? MAX_SECONDS;
  if (seconds < min) {
    return `That was ${Math.round(seconds)} seconds. Read for at least ${Math.round(min)}: about a minute makes the best voice.`;
  }
  if (seconds > max) return `That was too long. Keep it under ${Math.round(max / 60)} minutes.`;
  return null;
}

/** What the page says while a recording runs: how far it is, and when it is long enough. */
export function recordingLine(seconds: number, min: number = MIN_SECONDS): string {
  if (seconds < min) return `Keep reading: ${Math.ceil(min - seconds)} more seconds at least.`;
  if (seconds < AIM_SECONDS) return "Long enough to use. A full minute makes a better voice.";
  return "That is plenty. Stop when you reach the end of a sentence.";
}

/** How loud a moment of sound is, from 0 to 1, for the meter beside the clock. */
export function loudness(samples: Float32Array | readonly number[]): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += (samples[i] ?? 0) ** 2;
  // Speech sits far below full scale, so the root is stretched to fill the meter.
  return Math.min(1, Math.sqrt(sum / samples.length) * 4);
}

/** A person's own voice in a line: since when, and how long the recording was. */
export function ownVoiceLine(
  own: { created_at: string; seconds?: number | null },
  at: (iso: string) => string,
): string {
  const long = own.seconds ? `, from a recording of ${clock(own.seconds)}` : "";
  return `Made ${at(own.created_at)}${long}.`;
}

/**
 * Whose voice speaks a person's greetings, in a sentence for their Account page. Their own when
 * they have one; else the tenant's female or male voice, or its default.
 */
export function speaksLine(mine: { gender?: string | null; own_voice?: unknown | null }): string {
  if (mine.own_voice) return "Your voicemail greetings are spoken in your own voice.";
  if (mine.gender === "female" || mine.gender === "male") {
    return `Your voicemail greetings are spoken in your team's ${mine.gender} voice.`;
  }
  return "Your voicemail greetings are spoken in your team's default voice.";
}

/** Why a recording cannot be made here and now, or null when it can. */
export function cannotRecord(
  mine: { can_record: boolean } | undefined,
  browser: { microphone: boolean; recorder: boolean },
  signedInAs: boolean,
): string | null {
  if (signedInAs) return "You are signed in as somebody else. Only they can record their voice.";
  if (mine && !mine.can_record) {
    return "No ElevenLabs account is connected yet. An admin connects one under Settings, Voices.";
  }
  if (!browser.microphone || !browser.recorder) {
    return "This browser cannot record from a microphone. Use a current Chrome, Edge, Firefox or Safari.";
  }
  return null;
}
